import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import './App.css';
import './lib/style.css';
import * as Mui from '@mui/material';
import { Routes, Route, useLocation } from "react-router-dom";
import { useCookies } from 'react-cookie';
import packageInfo from '../package.json';
import RouteLoader from './lib/RouteLoader';
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Customers = lazy(() => import('./pages/Customers'));
const ServiceOrders = lazy(() => import('./pages/ServiceOrders'));
const Radius = lazy(() => import('./pages/devices/Radius'));
const SignIn = lazy(() => import('./pages/Signin'));
const BNG = lazy(() => import('./pages/devices/Bng'));
const Taxonomy = lazy(() => import('./pages/Taxonomy'));
const Page404 = lazy(() => import('./pages/Page404'));
const Products = lazy(() => import('./pages/Products'));
const Clusters = lazy(() => import('./pages/Clusters'));
const Brand = lazy(() => import('./pages/Brand'));
const Users = lazy(() => import('./pages/Users'));
const Olt = lazy(() => import('./pages/devices/Olt'));
const BillingTemplates = lazy(() => import('./pages/BillingTemplates'));
const DeviceType = lazy(() => import('./pages/DeviceType'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Pay = lazy(() => import('./pages/Pay'));
const InvoicePDF = lazy(() => import('./pages/InvoicePDF'));
const SignIn1 = lazy(() => import('./pages/Signin1'));
const Test = lazy(() => import('./pages/Test'));
const AuditLog = lazy(() => import('./pages/AuditLog'));

const normalizeEnvUrl = (value) => {
  if (!value) {
    return value;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `http://${value}`;
};

function App() {
  const [installPromptEvent, setInstallPromptEvent] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showRouteLoader, setShowRouteLoader] = useState(false);
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const appVersion = `ver. ${packageInfo.version}`;
  
  const config = { 
    appName: process.env.REACT_APP_NAME,
    appDesc: process.env.REACT_APP_DESC,
    appDomain: process.env.REACT_APP_DOMAIN,
    appTitle : process.env.REACT_APP_TITLE,
    locStorage : process.env.REACT_APP_STORAGE,
    locCookie : process.env.REACT_APP_COOKIE,
    imgdir : process.env.REACT_APP_IMGDIR,
    port : process.env.REACT_APP_PORT,
    home : normalizeEnvUrl(process.env.REACT_APP_HOME),
    hostApi : normalizeEnvUrl(process.env.REACT_APP_API),
    ver : appVersion
  }
  const [cookies] = useCookies([process.env.REACT_APP_COOKIE]);

  useEffect(() => {
    if (previousPathRef.current === location.pathname) {
      return;
    }

    previousPathRef.current = location.pathname;
    setShowRouteLoader(true);
    const hideTimer = window.setTimeout(() => {
      setShowRouteLoader(false);
    }, 720);

    return () => window.clearTimeout(hideTimer);
  }, [location.pathname]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return;
      }
      if (window.localStorage.getItem('netbilling-install-dismissed') === '1') {
        return;
      }
      setInstallPromptEvent(event);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const dismissInstallPrompt = () => {
    window.localStorage.setItem('netbilling-install-dismissed', '1');
    setShowInstallPrompt(false);
  };

  const installToDevice = async () => {
    if (!installPromptEvent) {
      setShowInstallPrompt(false);
      return;
    }

    installPromptEvent.prompt();
    const choice = await installPromptEvent.userChoice.catch(() => null);
    if (choice?.outcome !== 'accepted') {
      window.localStorage.setItem('netbilling-install-dismissed', '1');
    }
    setInstallPromptEvent(null);
    setShowInstallPrompt(false);
  };

  return (
    <>
      <RouteLoader open={showRouteLoader} label="Please wait..." appName={config.appName} appDesc={config.appDesc} />
      <Suspense fallback={<RouteLoader open={true} label="Loading page..." appName={config.appName} appDesc={config.appDesc} />}>
        <Routes>
          <Route path="*" element={<Page404 />} />
          <Route path="/" element={<Dashboard config={config} cookies={cookies} />} />
          
          <Route path="/signin" element={<SignIn config={config} />} />
          <Route path="/signin1" element={<SignIn1 config={config} />} />
          <Route path="/signout" element={<SignIn config={config} action="signout" />} />

          <Route path="/dashboard" element={<Dashboard config={config} />} />

          <Route path="/products" element={<Products config={config} />} />
          <Route path="/clusters" element={<Clusters config={config} />} />
          <Route path="/brand" element={<Brand config={config} />} />

          <Route path="/radius" element={<Radius config={config} />} /> 
          <Route path="/bng" element={<BNG config={config} />} />
          <Route path="/olt" element={<Olt config={config} />} />

          <Route path="/serviceorder" element={<ServiceOrders config={config} />} />
          <Route path="/customers" element={<Customers config={config}/>} />

          <Route path="/device-type" element={<DeviceType config={config} />} />
          <Route path="/users" element={<Users config={config} />} />
          <Route path="/taxo" element={<Taxonomy config={config} />} /> 

          <Route path="/billing-templates" element={<BillingTemplates config={config} />} />
          <Route path="/invoices" element={<Invoices config={config} />} />
          <Route path="/invoicepdf/:id" element={<InvoicePDF config={config} />} />

          <Route path="/pay/:id" element={<Pay config={config} />} /> 

          <Route path="/test" element={<Test config={config} />} />
          <Route path="/audit-log" element={<AuditLog config={config} />} />
        </Routes>
      </Suspense>

      {showInstallPrompt && (
        <Mui.Box
          sx={{
            position: 'fixed',
            left: 16,
            right: 16,
            bottom: 104,
            zIndex: 1400,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <Mui.Paper
            elevation={10}
            sx={{
              width: 'min(420px, 100%)',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              borderRadius: '20px',
              p: '16px 18px',
              border: '1px solid',
              borderColor: 'primary.dark',
              boxShadow: '0 18px 50px rgba(0,0,0,0.24)',
              pointerEvents: 'auto',
            }}
          >
            <Mui.Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <Mui.Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '14px',
                  background: 'rgba(255,255,255,0.08)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M4 7v2c0 .55-.45 1-1 1H2v4h1c.55 0 1 .45 1 1v2c0 1.65 1.35 3 3 3h3v-2H7c-.55 0-1-.45-1-1v-2c0-1.3-.84-2.42-2-2.83v-.34C5.16 11.42 6 10.3 6 9V7c0-.55.45-1 1-1h3V4H7C5.35 4 4 5.35 4 7m17 3c-.55 0-1-.45-1-1V7c0-1.65-1.35-3-3-3h-3v2h3c.55 0 1 .45 1 1v2c0 1.3.84 2.42 2 2.83v.34c-1.16.41-2 1.52-2 2.83v2c0 .55-.45 1-1 1h-3v2h3c1.65 0 3-1.35 3-3v-2c0-.55.45-1 1-1h1v-4z" />
                </svg>
              </Mui.Box>
              <Mui.Box sx={{ flex: 1 }}>
                <Mui.Typography sx={{ fontWeight: 700, mb: 0.5 }}>
                  Install NetBilling ke device?
                </Mui.Typography>
                <Mui.Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.45 }}>
                  Tambahkan NetBilling ke layar utama supaya aksesnya tetap ringkas dan konsisten di device Anda.
                </Mui.Typography>
                <Mui.Box sx={{ display: 'flex', gap: 1.25, mt: 1.75, flexWrap: 'wrap' }}>
                  <Mui.Button
                    type="button"
                    onClick={installToDevice}
                    variant="contained"
                    sx={{
                      borderRadius: '999px',
                      bgcolor: '#ffffff',
                      color: 'primary.main',
                      fontWeight: 700,
                      px: 2,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.92)',
                      },
                    }}
                  >
                    Install
                  </Mui.Button>
                  <Mui.Button
                    type="button"
                    onClick={dismissInstallPrompt}
                    variant="outlined"
                    sx={{
                      borderRadius: '999px',
                      borderColor: 'rgba(255,255,255,0.24)',
                      color: '#ffffff',
                      fontWeight: 600,
                      px: 2,
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.4)',
                        backgroundColor: 'rgba(255,255,255,0.06)',
                      },
                    }}
                  >
                    Nanti saja
                  </Mui.Button>
                </Mui.Box>
              </Mui.Box>
            </Mui.Box>
          </Mui.Paper>
        </Mui.Box>
      )}
    </>
  );
}

export default App;
